import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { Text } from '../../core/Text/Text.js';
import { FitText } from '../../core/FitText/FitText.js';
import { Badge } from '../../core/Badge/Badge.js';
import './KpiFilterRow.css';

export interface KpiFilterItem {
  /** Unique id used as the filter token. */
  id: string;
  /** Short label (eg "Active"). */
  label: ReactNode;
  /** Count value to display. */
  value: ReactNode;
  /** Optional tone: drives the indicator color. */
  tone?: 'healthy' | 'degraded' | 'warning' | 'down' | 'inactive' | 'pending' | 'neutral' | 'primary';
  /** Hide the indicator dot. */
  noDot?: boolean;
  /** A small tinted tile in the card's top row, in place of the dot. */
  icon?: ReactNode;
  /** One muted line under the count, for a share or a qualifier. */
  hint?: ReactNode;
}

export interface KpiFilterRowProps extends Omit<BaseProps, 'color'> {
  items: KpiFilterItem[];
  /** Currently-active filter id (single-select). */
  value?: string | null;
  /** Fires with the new active id, or `null` if the user toggled the active off. */
  onChange?: (next: string | null) => void;
  /**
   * The badge on the selected card. English by default, which made the design
   * system itself break the rule that all copy comes from a catalogue; a
   * translated console passes its own word.
   */
  filteringLabel?: string;
}

const TONE_COLOR: Record<NonNullable<KpiFilterItem['tone']>, string> = {
  healthy: 'var(--rr-color-green-500)',
  degraded: 'var(--rr-color-yellow-500)',
  warning: 'var(--rr-color-orange-500)',
  down: 'var(--rr-color-red-500)',
  inactive: 'var(--rr-color-stone-400)',
  pending: 'var(--rr-color-blue-500)',
  neutral: 'var(--rr-color-stone-400)',
  primary: 'var(--rr-color-primary)'
};

/**
 * A horizontal row of clickable KPI cards that double as quick filters.
 * Each card shows a label + count; clicking one passes its `id` to
 * `onChange`. Clicking the active card again clears the filter.
 *
 *   <KpiFilterRow
 *     items={[
 *       { id: 'all',      label: 'All',      value: 66 },
 *       { id: 'active',   label: 'Active',   value: 60, tone: 'healthy' },
 *       { id: 'inactive', label: 'Inactive', value: 6,  tone: 'inactive' }
 *     ]}
 *     value={filter}
 *     onChange={setFilter}
 *   />
 */
export const KpiFilterRow = forwardRef(function KpiFilterRow(
  { items, value, onChange, className, filteringLabel = 'Filtering', ...rest }: KpiFilterRowProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  return (
    <div
      ref={ref}
      className={['rr-kpi-filter-row', className].filter(Boolean).join(' ')}
      role="tablist"
      aria-orientation="horizontal"
      {...rest}
    >
      {items.map((item) => {
        const active = value === item.id;
        const tone = item.tone ?? (item.id === 'all' ? 'primary' : 'neutral');
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={['rr-kpi-filter-card', active && 'rr-kpi-filter-card--active'].filter(Boolean).join(' ')}
            onClick={() => onChange?.(active ? null : item.id)}
          >
            <div className="rr-kpi-filter-card__top">
              {/* An icon tile stands in for the dot where one is given: the
                  designs put a tinted tile on cards that are also filters. */}
              {item.icon ? (
                <span
                  className="rr-kpi-filter-card__icon"
                  style={{ color: TONE_COLOR[tone], background: `color-mix(in srgb, ${TONE_COLOR[tone]} 12%, transparent)` }}
                  aria-hidden
                >
                  {item.icon}
                </span>
              ) : !item.noDot ? (
                <span
                  className="rr-kpi-filter-card__dot"
                  style={{ background: TONE_COLOR[tone] }}
                  aria-hidden
                />
              ) : null}
              <Text role="overline" className="rr-kpi-filter-card__label">{item.label}</Text>
            </div>
            <div className="rr-kpi-filter-card__value">
              <Text
                role="headline"
                fw={700}
                c={item.tone && item.tone !== 'neutral' ? TONE_COLOR[item.tone] : undefined}
                style={{ minWidth: 0, flex: '1 1 auto' }}
              >
                <FitText>{item.value}</FitText>
              </Text>
              {active && <Badge size="xs" variant="light" color="orange">{filteringLabel}</Badge>}
            </div>
            {item.hint && (
              <Text role="caption" className="rr-kpi-filter-card__hint">{item.hint}</Text>
            )}
          </button>
        );
      })}
    </div>
  );
});
